import { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import * as uuid from 'uuid';
import styled from 'styled-components';

import type { ReschedulingAppointmentSelectedMessage } from '../../../../../../common/rescheduling';
import { ConversationContext } from '../../../../../../context/conversationContext';
import EhrTimeSlots from './ehrTimeSlots';

import * as api from '../../../../../../api';
import MessageContent from '../../../Message/messageContent';
import { ConfigurationContext } from '../../../../../../context/configurationContext';
import Appointment from '../../GeneralUi/Appointment';

import { DateRangeFilter } from 'common/dist/components';

interface Props {
  message: ReschedulingAppointmentSelectedMessage;
  showTextAnimation: boolean;
}

interface DateRangeFilters {
  startDay: Date | null;
  endDay: Date | null;
  startTime?: Date;
  endTime?: Date;
}

const AppointmentRescheduling: React.FC<Props> = ({
  message,
  showTextAnimation,
}) => {
  const [ehrTimeSlots, setEhrTimeSlots] = useState<
    api.EhrTimeSlot.EhrTimeSlot[]
  >([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState<DateRangeFilters>({
    startDay: null,
    endDay: null,
  });
  const conversationContext = useContext(ConversationContext);
  const configurationContext = useContext(ConfigurationContext);
  const { appointment } = message.data;

  const loadEhrTimeSlots = useCallback(async () => {
    if (!configurationContext?.id) return;
    setIsLoadingSlots(true);
    try {
      const { availableEhrTimeSlots } =
        await api.client.RescheduleEhrTimeSlot.getRescheduleSlots({
          appointmentEhrId: appointment.id,
          patientEhrId: appointment.patientEhrId,
          assistantId: configurationContext.id,
          practiceId: appointment.practice.id,
          providerId: appointment.provider?.id,
          startTime: filters.startDay || undefined,
          endTime: filters.endDay || undefined,
        });
      setEhrTimeSlots(availableEhrTimeSlots);
    } catch (error) {
      console.error(error);
    }
    setIsLoadingSlots(false);
  }, [
    appointment.id,
    appointment.patientEhrId,
    appointment.practice,
    appointment.provider,
    configurationContext?.id,
    filters,
  ]);

  useEffect(() => {
    if (!appointment.isReschedulingEnabled) return;
    loadEhrTimeSlots();
  }, [appointment.isReschedulingEnabled, loadEhrTimeSlots]);

  const handleAppointmentSelected = async (
    slot: api.EhrTimeSlot.EhrTimeSlot,
  ) => {
    if (isSubmitting) return;
    if (!conversationContext) return;
    if (!configurationContext?.id) return;
    setIsSubmitting(true);
    let confirmationInformation:
      | api.EhrTimeSlot.TimeSlotConfirmation
      | undefined;
    try {
      ({ confirmationInformation } =
        await api.client.RescheduleEhrTimeSlot.reschedule({
          ..._.pick(
            slot,
            'startTime',
            'endTime',
            'appointmentKind',
            'practiceId',
            'providerId',
          ),
          newSlotId: slot.id,
          patientEhrId: appointment.patientEhrId,
          appointmentEhrId: appointment.id,
          assistantId: configurationContext.id,
        }));
    } catch (error) {
      console.error(error);
    }
    if (!confirmationInformation) {
      setIsSubmitting(false);
      return;
    }
    conversationContext.disableMessages();
    conversationContext.upsertLastMessage(
      {
        ...message,
        done: true,
        chat_message_id: uuid.v4(),
        answer: `Appointment rescheduled successfully.`,
        intentComponent:
          api.Assistant.IntentComponent.ReschedulingAppointmentActionConfirmed,
        data: { rescheduled: confirmationInformation },
      },
      api.Assistant.IntentComponent.ReschedulingAppointmentActionConfirmed,
    );
    setIsSubmitting(false);
  };

  return (
    <>
      <MessageContent
        padTop
        content={message.answer}
        isAgent={true}
        isLoading={false}
        showTextAnimation={showTextAnimation}
      />
      <div data-test-id="cancel-reschedule-rescheduling">
        <Appointment
          appointment={appointment}
          disabled={message.disabled ?? false}
          actionButtonLabel={
            appointment.isCancelEnabled && appointment.isReschedulingEnabled
              ? 'Cancel appointment'
              : ''
          }
          onActionButtonClicked={() => {
            if (!conversationContext) return;
            conversationContext.disableMessages();
            conversationContext.upsertLastMessage(
              {
                ...message,
                done: true,
                chat_message_id: uuid.v4(),
                answer: `Are you sure you want to cancel? This action cannot be undone, and you will lose this appointment time.`,
                intentComponent:
                  api.Assistant.IntentComponent
                    .ReschedulingAppointmentCancelling,
                data: { appointment },
              },
              api.Assistant.IntentComponent
                .ReschedulingAppointmentActionConfirmed,
            );
          }}
        />
        {appointment.isReschedulingEnabled ? (
          <TimeSlotWrapper>
            <DateRangeFilterWrapper>
              <DateRangeFilter
                startDay={filters.startDay}
                endDay={filters.endDay}
                onChange={(startDay: Date | null, endDay: Date | null) => {
                  setFilters({
                    startDay,
                    endDay,
                  });
                }}
              />
            </DateRangeFilterWrapper>
            <EhrTimeSlots
              timezone={appointment.practice.timezone}
              slots={ehrTimeSlots}
              onAppointmentSelected={handleAppointmentSelected}
              isLoading={isLoadingSlots}
              disabled={(isSubmitting || message.disabled) ?? false}
            />
          </TimeSlotWrapper>
        ) : null}
      </div>
    </>
  );
};

const TimeSlotWrapper = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;
`;

const DateRangeFilterWrapper = styled.div`
  margin: 24px 0;
`;

export default AppointmentRescheduling;
